import React, { useState } from 'react';
import CaptureModal from './CaptureModal';
import { validateVaillantSerialNumber } from '../utils/vaillantSerialNumber';
import { extractSerialNumberFromUpload } from '../services/api/serialNumber';

interface SeriennummerModalProps {
  onClose: () => void;
  onSubmit: (serialNumber: string, imageData: string | null) => void;
  initialValue?: string;
}

const SeriennummerModal: React.FC<SeriennummerModalProps> = ({ onClose, onSubmit, initialValue = '' }) => {
  const [imageData, setImageData] = useState<string | null>(null);
  const [serialNumber, setSerialNumber] = useState(initialValue);
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [aiNotFound, setAiNotFound] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  const trimmed = serialNumber.trim();
  const formatValid = trimmed.length === 0 || validateVaillantSerialNumber(trimmed).valid;
  const canSubmit = (!!imageData || trimmed.length >= 10) && formatValid && trimmed.length > 0 && !isChecking;

  const base64ToFile = (dataUrl: string, filename: string): File => {
    const [header, data] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
    const bytes = atob(data);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new File([arr], filename, { type: mime });
  };

  const runAiCheck = async (file: File) => {
    setIsChecking(true);
    setCheckError(null);
    setAiNotFound(false);

    try {
      const result = await extractSerialNumberFromUpload(file);
      const extracted = result.serialNumber || '';
      setSerialNumber(extracted);
      if (!extracted) setAiNotFound(true);
    } catch (err: any) {
      console.error('[SeriennummerModal] Fehler:', err);
      setCheckError(err.message || 'Fehler beim Prüfen des Bildes');
    } finally {
      setIsChecking(false);
    }
  };

  const handleCameraCapture = (captured: string) => {
    setShowCamera(false);
    setCheckError(null);
    setAiNotFound(false);
    setSerialNumber('');
    setImageData(captured);
    runAiCheck(base64ToFile(captured, 'foto.png'));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed, imageData);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-0 sm:px-4">
        <div className="w-full sm:max-w-md bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[90dvh] overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Seriennummer Außeneinheit
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 dark:hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Schließen"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">

            {/* Bild-Preview mit Spinner-Overlay */}
            <div
              className="relative w-full rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900"
              style={{ minHeight: '200px' }}
            >
              {imageData ? (
                <>
                  <img
                    src={imageData}
                    alt="Vorschau Seriennummer"
                    className="w-full h-full object-contain max-h-64"
                  />
                  {isChecking && (
                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/40 border-t-white" />
                      <span className="text-white text-sm font-medium">KI analysiert Bild...</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400 dark:text-gray-500 py-8 px-4 text-center">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">Noch kein Foto hochgeladen</span>
                </div>
              )}
            </div>

            {/* Foto-Button */}
            <button
              onClick={() => setShowCamera(true)}
              disabled={isChecking}
              className={`w-full min-h-[48px] flex items-center justify-center gap-2 rounded-xl border-2 font-medium transition-colors ${
                isChecking
                  ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {imageData ? 'Anderes Foto' : 'Foto hinzufügen'}
            </button>

            {checkError && (
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-lg px-4 py-2">
                {checkError}
              </p>
            )}

            {aiNotFound && !checkError && (
              <p className="text-sm text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-4 py-2">
                KI konnte keine Seriennummer erkennen. Bitte manuell eingeben.
              </p>
            )}

            {/* Seriennummer Text-Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Seriennummer
              </label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="z. B. 21054500100028300006000001N4"
                disabled={isChecking}
                className={`w-full rounded-xl border bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50 ${
                  trimmed.length > 0 && !formatValid
                    ? 'border-amber-400 dark:border-amber-500 focus:ring-amber-400'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                }`}
              />
              {trimmed.length > 0 && !formatValid && (
                <p className="mt-1.5 text-sm text-amber-700 dark:text-amber-400">
                  Die eingegebene Seriennummer entspricht keinem bekannten Vaillant-Format.
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full min-h-[52px] rounded-xl font-semibold text-base transition-colors ${
                canSubmit
                  ? 'bg-primary-600 hover:bg-primary-700 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              Senden
            </button>
          </div>
        </div>
      </div>

      {showCamera && (
        <CaptureModal
          isOpen={showCamera}
          onClose={() => setShowCamera(false)}
          onCapture={handleCameraCapture}
          title="Foto aufnehmen"
        />
      )}
    </>
  );
};

export default SeriennummerModal;
